import React from 'react';
import { Icon } from '@components/atoms/Icon.tsx';
import { IconType } from '@src/types/IconType.ts';
import { Info } from '@components/atoms/Info.tsx';
import { classNames } from '@utils/classNames.ts';
import { ParameterStatusType } from '@components/atoms/ParameterStatusType.ts';
import { StyledMarkdown } from '@components/atoms/StyledMarkdown.tsx';

interface Label {
  text: string;
  isActive?: boolean;
}

interface ParametersStatusMessageProps {
  type: ParameterStatusType;
  title: string;
  description: string;
  percentage?: string;
  percentageDescription?: string;
  labels: Label[];
  onInfoTap: () => void;
}

export const ParametersStatusMessage: React.FC<
  ParametersStatusMessageProps
> = ({
  type,
  title,
  description,
  percentage,
  percentageDescription,
  onInfoTap,
  labels,
}) => {
  const isWarning = type === ParameterStatusType.WARNING;
  const isError = type === ParameterStatusType.ERROR;
  const isUnset = type === ParameterStatusType.UNSET;
  let messageBoxStyle = 'bg-green text-white';
  let activeLabelStyle = 'bg-green-10 border-green text-green';
  if (isError) {
    messageBoxStyle = 'bg-error text-white';
    activeLabelStyle = 'bg-red-10 border-error text-error';
  } else if (isWarning) {
    messageBoxStyle = 'bg-orange text-white';
    activeLabelStyle = 'bg-orange-10 border-orange text-orange';
  } else {
    if (isUnset) {
      messageBoxStyle = 'bg-grey3light text-black';
      activeLabelStyle = 'bg-grey3light text-dark';
    }
  }

  return (
    <div className="w-full font-sans">
      <div className={classNames('p-6 rounded-lg mb-2 ', messageBoxStyle)}>
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 flex items-center">
            <Icon
              type={
                isWarning || isError || isUnset
                  ? IconType.ExclamationMark
                  : IconType.Check
              }
              className="w-16 h-16"
            />
          </div>
          <div className="flex-grow">
            <div className="space-y-2">
              <div
                className="font-semibold text-base flex items-center gap-1"
                onClick={onInfoTap}
              >
                {title}
                {!isUnset && (
                  <span>
                    <Info
                      className="bg-white w-3.5 h-3.5"
                      onClick={() => {
                        onInfoTap();
                      }}
                    />
                  </span>
                )}
              </div>
              {(percentage || percentageDescription) && (
                <div>
                  <span className={'font-semibold text-base'}>
                    {percentage}
                  </span>
                  <span> </span>
                  <span className={'font-thin'}>{percentageDescription}</span>
                </div>
              )}
              {description && (
                <p className="text-sm leading-6 m-0 font-thin">
                  <StyledMarkdown markdown={description} />
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className={classNames(
          'rounded-bonusLarge p-0.5',
          labels.length % 2 === 0
            ? 'grid grid-cols-2 md:grid-cols-4 gap-2'
            : 'flex flex-row space-x-2',
        )}
      >
        {labels.map((label, index) => (
          <div
            key={index}
            className={classNames(
              'flex-1 px-4 py-2 text-sm rounded-bonusLarge text-center flex items-center justify-center min-h-[40px]',
              label.isActive && activeLabelStyle,
              label.isActive && 'border-solid border-2 font-medium',
              !label.isActive && 'text-dark bg-grey3light',
            )}
          >
            {label.text}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ParametersStatusMessage;
